import {
  TIMELINE_BOARD_KEYS,
  type TimelineBoardKey,
  type WorkspaceLimits,
  type WorkspaceLimitKey,
} from '../types/schema'
import {
  normalizeChecklistInputLimits,
  type ChecklistInputLimits,
} from './checklistLimits'
import { WORKSPACE_LIMIT_HARD_CAPS, BOARD_SCOPED_LIMIT_KEYS } from '../config/workspaceLimitCaps'
export { WORKSPACE_LIMIT_HARD_CAPS, BOARD_SCOPED_LIMIT_KEYS } from '../config/workspaceLimitCaps'
import {
  normalizeTierId,
  resolveTeamWorkspaceCaps,
  resolveTeamWorkspaceDefaults,
  resolvePersonalWorkspaceDefaults,
  WORKSPACE_COUNT_LIMIT_KEYS,
  getPlanLimitOverrides,
  type WorkspaceCountLimitKey,
  type PlanLimitOverrides,
} from '../config/planLimitsCore'

export const TEAM_SOFT_LIMIT_RATIO = 0.25
export const ADMIN_SOFT_LIMIT_RATIO = 0.4

export const getWorkspaceLimitDraftValue = (val: unknown): string =>
  String(val ?? '')

export type WorkspaceLimitDefinition = {
  key: WorkspaceLimitKey
  label: string
  suffix: string
  scope: 'board' | 'parent' | 'workspace' | 'input' | 'ai'
}

export const WORKSPACE_LIMIT_DEFINITIONS: WorkspaceLimitDefinition[] = [
  { key: 'maxGoalsPerBoard', label: 'Goals per board', suffix: 'goals', scope: 'board' },
  { key: 'maxTasksPerBoard', label: 'Tasks per board', suffix: 'tasks', scope: 'board' },
  { key: 'maxTasksPerGoal', label: 'Tasks inside one goal', suffix: 'tasks', scope: 'parent' },
  { key: 'maxSubtasksPerBoard', label: 'Subtasks per board', suffix: 'subtasks', scope: 'board' },
  { key: 'maxSubtasksPerTask', label: 'Subtasks inside one task', suffix: 'subtasks', scope: 'parent' },
  { key: 'maxChecklistsPerBoard', label: 'Checklist files per board', suffix: 'files', scope: 'board' },
  { key: 'maxChecklistsPerContainer', label: 'Checklist files per folder', suffix: 'files', scope: 'parent' },
  { key: 'maxWorkspaceMembers', label: 'Workspace members', suffix: 'members', scope: 'workspace' },
  { key: 'maxPendingInvites', label: 'Pending invites', suffix: 'invites', scope: 'workspace' },
  { key: 'maxEnabledDashboardViews', label: 'Enabled dashboard views', suffix: 'views', scope: 'workspace' },
  { key: 'maxAiPromptCharacters', label: 'AI prompt characters', suffix: 'chars', scope: 'ai' },
  { key: 'titleMaxLength', label: 'Checklist title characters', suffix: 'chars', scope: 'input' },
  { key: 'commentMaxLength', label: 'Discussion comment characters', suffix: 'chars', scope: 'input' },
  { key: 'searchMaxLength', label: 'Search field characters', suffix: 'chars', scope: 'input' },
  { key: 'autoSpawnTitleMaxLength', label: 'Auto-spawn title characters', suffix: 'chars', scope: 'input' },
  { key: 'weightageMax', label: 'Checklist weightage', suffix: 'max', scope: 'input' },
  { key: 'estimatedHoursMax', label: 'Estimated effort hours', suffix: 'hours', scope: 'input' },
  { key: 'actualCostMax', label: 'Expense outlay', suffix: 'currency', scope: 'input' },
  { key: 'countdownHoursMax', label: 'Countdown window', suffix: 'hours', scope: 'input' },
  { key: 'delayHoursMax', label: 'Delay duration', suffix: 'hours', scope: 'input' },
  { key: 'autoSpawnYOffsetMax', label: 'Auto-spawn Y offset', suffix: 'px', scope: 'input' },
]


const SPECIAL_TEAM_DEFAULTS: Partial<Record<WorkspaceLimitKey, number>> = {
  // Team defaults without plan override = hard cap for goals and AI prompt characters;
  // per-plan limits and caps resolve via resolveTeamWorkspaceDefaults and resolveTeamWorkspaceCaps (owner-console-revamp/28).
  maxGoalsPerBoard: 10,
  maxAiPromptCharacters: 5_000,
}

const SPECIAL_PERSONAL_DEFAULTS: Partial<Record<WorkspaceLimitKey, number>> = {}

const getRatioDefaults = (ratio: number): Record<WorkspaceLimitKey, number> =>
  Object.fromEntries(
    (Object.keys(WORKSPACE_LIMIT_HARD_CAPS) as WorkspaceLimitKey[]).map((key) => [
      key,
      Math.max(1, Math.round(WORKSPACE_LIMIT_HARD_CAPS[key] * ratio)),
    ]),
  ) as Record<WorkspaceLimitKey, number>

export const TEAM_WORKSPACE_LIMIT_DEFAULTS: Record<WorkspaceLimitKey, number> = {
  ...getRatioDefaults(TEAM_SOFT_LIMIT_RATIO),
  ...SPECIAL_TEAM_DEFAULTS,
  ...resolveTeamWorkspaceDefaults('FREE_TIER'),
}

export const PERSONAL_WORKSPACE_LIMIT_DEFAULTS: Record<WorkspaceLimitKey, number> = {
  ...getRatioDefaults(0.5),
  ...SPECIAL_PERSONAL_DEFAULTS,
  ...resolvePersonalWorkspaceDefaults(),
}

export const getAdminEditableMaximum = (
  key: WorkspaceLimitKey,
  plan?: unknown,
  overrides: PlanLimitOverrides = getPlanLimitOverrides(),
): number => {
  if ((WORKSPACE_COUNT_LIMIT_KEYS as readonly string[]).includes(key)) {
    const tier = normalizeTierId(plan)
    const caps = resolveTeamWorkspaceCaps(tier, overrides)
    return caps[key as WorkspaceCountLimitKey]
  }
  const hardMaximum = WORKSPACE_LIMIT_HARD_CAPS[key]
  if (key === 'maxAiPromptCharacters' || key === 'maxGoalsPerBoard') {
    return hardMaximum
  }
  return Math.max(1, Math.round(hardMaximum * ADMIN_SOFT_LIMIT_RATIO))
}

const clampLimit = (
  key: WorkspaceLimitKey,
  value: unknown,
  fallback: number,
  adminEditable = false,
  plan?: unknown,
  overrides: PlanLimitOverrides = getPlanLimitOverrides(),
) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
  const rounded = Math.round(value)
  const hardMaximum = WORKSPACE_LIMIT_HARD_CAPS[key]
  const allowedMaximum = adminEditable
    ? getAdminEditableMaximum(key, plan, overrides)
    : hardMaximum

  if (rounded === -1) {
    return allowedMaximum === -1 ? -1 : allowedMaximum
  }
  if (allowedMaximum === -1) {
    return Math.max(1, rounded)
  }
  return Math.max(1, Math.min(allowedMaximum, rounded))
}

const normalizeLimitRecord = (
  value: unknown,
  fallback: Record<WorkspaceLimitKey, number>,
  adminEditable: boolean,
  plan?: unknown,
  overrides: PlanLimitOverrides = getPlanLimitOverrides(),
) => {
  const candidate = value && typeof value === 'object' ? value as Partial<Record<WorkspaceLimitKey, number>> : {}
  return Object.fromEntries(
    (Object.keys(WORKSPACE_LIMIT_HARD_CAPS) as WorkspaceLimitKey[]).map((key) => [
      key,
      clampLimit(key, candidate[key], fallback[key], adminEditable, plan, overrides),
    ]),
  ) as Record<WorkspaceLimitKey, number>
}

export const limitsToChecklistInputLimits = (
  limits: Record<WorkspaceLimitKey, number>,
): ChecklistInputLimits => normalizeChecklistInputLimits({
  titleMaxLength: limits.titleMaxLength,
  commentMaxLength: limits.commentMaxLength,
  searchMaxLength: limits.searchMaxLength,
  autoSpawnTitleMaxLength: limits.autoSpawnTitleMaxLength,
  weightageMax: limits.weightageMax,
  estimatedHoursMax: limits.estimatedHoursMax,
  actualCostMax: limits.actualCostMax,
  countdownHoursMax: limits.countdownHoursMax,
  delayHoursMax: limits.delayHoursMax,
  autoSpawnYOffsetMax: limits.autoSpawnYOffsetMax,
}, false)

export const createDefaultWorkspaceLimits = (
  isPersonalWorkspace: boolean,
  plan?: unknown,
  overrides: PlanLimitOverrides = getPlanLimitOverrides(),
): WorkspaceLimits => {
  let softCaps: Record<WorkspaceLimitKey, number>
  if (isPersonalWorkspace) {
    const tier = plan ? normalizeTierId(plan) : undefined
    const personalDefaults = resolvePersonalWorkspaceDefaults(tier, overrides)
    softCaps = {
      ...PERSONAL_WORKSPACE_LIMIT_DEFAULTS,
      ...personalDefaults,
    }
  } else {
    const tier = normalizeTierId(plan)
    const teamDefaults = resolveTeamWorkspaceDefaults(tier, overrides)
    softCaps = {
      ...TEAM_WORKSPACE_LIMIT_DEFAULTS,
      ...teamDefaults,
    }
  }
  return {
    hardCaps: WORKSPACE_LIMIT_HARD_CAPS,
    softCaps,
    sameForAllBoards: true,
    boardSoftCaps: {},
    input: limitsToChecklistInputLimits(softCaps),
  }
}

export const normalizeWorkspaceLimits = (
  value: unknown,
  legacyChecklistInputLimits: unknown,
  isPersonalWorkspace: boolean,
  plan?: unknown,
  overrides: PlanLimitOverrides = getPlanLimitOverrides(),
): WorkspaceLimits => {
  const defaults = createDefaultWorkspaceLimits(isPersonalWorkspace, plan, overrides)
  const candidate = value && typeof value === 'object' ? value as Partial<WorkspaceLimits> : {}
  const softCaps = normalizeLimitRecord(
    candidate.softCaps,
    defaults.softCaps,
    !isPersonalWorkspace,
    plan,
    overrides,
  )
  const legacyChecklist = normalizeChecklistInputLimits(legacyChecklistInputLimits, isPersonalWorkspace)

  if (candidate.input || legacyChecklistInputLimits) {
    const input = normalizeChecklistInputLimits(candidate.input ?? legacyChecklist, isPersonalWorkspace)
    softCaps.titleMaxLength = input.titleMaxLength
    softCaps.commentMaxLength = input.commentMaxLength
    softCaps.searchMaxLength = input.searchMaxLength
    softCaps.autoSpawnTitleMaxLength = input.autoSpawnTitleMaxLength
    softCaps.weightageMax = input.weightageMax
    softCaps.estimatedHoursMax = input.estimatedHoursMax
    softCaps.actualCostMax = input.actualCostMax
    softCaps.countdownHoursMax = input.countdownHoursMax
    softCaps.delayHoursMax = input.delayHoursMax
    softCaps.autoSpawnYOffsetMax = input.autoSpawnYOffsetMax
  }

  const boardSoftCaps: WorkspaceLimits['boardSoftCaps'] = {}
  const rawBoardCaps = candidate.boardSoftCaps && typeof candidate.boardSoftCaps === 'object'
    ? candidate.boardSoftCaps
    : {}
  for (const board of TIMELINE_BOARD_KEYS) {
    const boardRecord = (rawBoardCaps as Partial<Record<TimelineBoardKey, unknown>>)[board]
    if (!boardRecord) continue
    const normalized = normalizeLimitRecord(
      boardRecord,
      softCaps,
      !isPersonalWorkspace,
      plan,
      overrides,
    )
    boardSoftCaps[board] = normalized
  }

  return {
    hardCaps: WORKSPACE_LIMIT_HARD_CAPS,
    softCaps,
    sameForAllBoards: candidate.sameForAllBoards !== false,
    boardSoftCaps,
    input: limitsToChecklistInputLimits(softCaps),
  }
}

export const getWorkspaceLimitValue = (
  limits: WorkspaceLimits,
  key: WorkspaceLimitKey,
  board?: TimelineBoardKey,
) => {
  if (
    board &&
    limits.sameForAllBoards === false &&
    BOARD_SCOPED_LIMIT_KEYS.includes(key) &&
    limits.boardSoftCaps?.[board]?.[key]
  ) {
    return limits.boardSoftCaps[board]?.[key] ?? limits.softCaps[key]
  }
  return limits.softCaps[key]
}

export const USER_WORKSPACE_LIMITS = {
  maxNonOwnedWorkspaces: 20,
} as const

export const getMaxNonOwnedWorkspaces = (): number => USER_WORKSPACE_LIMITS.maxNonOwnedWorkspaces
